(() => {
  if (window.ToolboxCartValidationModalInitialized) return;
  window.ToolboxCartValidationModalInitialized = true;

  const modalSelector = '[data-company-purchase-modal]';
  const actionRequestTimeout = 15000;
  let lastFocusedElement = null;
  let modalAcknowledged = false;
  let modalActionState = null;
  let modalActionInProgress = false;

  const groupDefinitions = [
    {
      key: 'context',
      title: 'Item retirado'
    },
    {
      key: 'purchased',
      title: 'Produtos já adquiridos'
    },
    {
      key: 'missingMoodle',
      title: 'Produtos com cadastro incompleto'
    },
    {
      key: 'duplicates',
      title: 'Itens duplicados'
    },
    {
      key: 'adjusted',
      title: 'Quantidades ajustadas'
    },
  ];

  function completeAcknowledgement(action, hasChanges) {
    if (modalAcknowledged) return;
    modalAcknowledged = true;

    const result = { action, hasChanges };

    if (typeof window.ToolboxCompleteCartValidationAcknowledgement === 'function') {
      window.ToolboxCompleteCartValidationAcknowledgement(result);
      return;
    }

    window.ToolboxLastCartValidationAcknowledgement = result;
    window.ToolboxCartValidationAcknowledgementPromise = Promise.resolve(result);
    document.dispatchEvent(
      new CustomEvent('toolbox:cart-validation:acknowledged', { detail: result })
    );
  }

  function closeModal(modal, action = 'stay') {
    if (!modal || modal.hidden) return;

    modal.classList.remove('is-open');
    modal.hidden = true;
    document.body.classList.remove('overflow-hidden');
    completeAcknowledgement(action, true);

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  function navigateTo(url) {
    window.location.assign(url || '/');
  }

  function captureRegistrationContext(modal) {
    return {
      cartContext: String(modal.dataset.cartContext || '').trim().toLowerCase(),
    };
  }

  async function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), actionRequestTimeout);

    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  async function prepareGuestContext(profile) {
    if (!window.ElevaContext?.prepareGuest) {
      throw new Error('Gerenciador de contexto indisponível.');
    }

    return window.ElevaContext.prepareGuest(profile);
  }

  function setModalError(modal, message = '') {
    const errorElement = modal.querySelector('[data-cart-validation-error]');
    if (!errorElement) return;

    errorElement.textContent = message;
    errorElement.hidden = !message;
  }

  function setModalProcessing(modal, isProcessing) {
    const processingElement = modal.querySelector('[data-cart-validation-processing]');
    const controls = modal.querySelectorAll(
      '[data-cart-validation-continue], [data-cart-validation-alternate], [data-company-purchase-modal-close]'
    );

    modal.classList.toggle('is-processing', isProcessing);
    processingElement.hidden = !isProcessing;
    controls.forEach((control) => {
      control.disabled = isProcessing;
    });

    if (isProcessing) {
      setModalError(modal);
      modal.setAttribute('aria-busy', 'true');
      modal.setAttribute('aria-labelledby', 'company-purchase-modal-processing-title');
      modal.setAttribute('aria-describedby', 'company-purchase-modal-processing-description');
      processingElement.focus({ preventScroll: true });
      return;
    }

    modal.removeAttribute('aria-busy');
    modal.setAttribute('aria-labelledby', 'company-purchase-modal-title');
    modal.setAttribute('aria-describedby', 'company-purchase-modal-description');
  }

  function getModalActions(modal, groupedResults, purchaseResult) {
    const customerType = String(purchaseResult?.customerType || '').trim().toUpperCase();
    const remainingItemCount = Number(purchaseResult?.cart?.item_count || 0);
    const checkoutRequested = new URLSearchParams(window.location.search).get('checkout_after_login') === 'true';
    const homeUrl = modal.dataset.homeUrl || '/';
    const continueAction = {
      type: 'continue',
      url: checkoutRequested ? null : homeUrl,
    };

    if (customerType === 'PJ' && groupedResults.context.length > 0) {
      return {
        checkoutRequested,
        alternate: {
          label: 'Voltar',
          type: 'navigate',
          url: homeUrl,
        },
        continue: {
          label: 'Criar cadastro com trabalhador',
          type: 'logout-and-navigate',
          url: modal.dataset.registerPfUrl || '/account/register',
          logoutUrl: modal.dataset.logoutUrl || '/account/logout',
          cartUpdateUrl: modal.dataset.cartUpdateUrl || '/cart/update.js',
          registrationType: 'PF',
        },
      };
    }

    if (customerType === 'PF' && groupedResults.purchased.length > 0) {
      const moodleAction = {
        label: 'Acessar a Escola Digital',
        type: 'navigate',
        url: modal.dataset.moodleUrl || 'https://moodle.senairs.org.br/login/index.php',
      };

      if (remainingItemCount > 0) {
        return {
          checkoutRequested,
          alternate: moodleAction,
          continue: {
            label: 'Continuar',
            ...continueAction,
          },
        };
      }

      return {
        checkoutRequested,
        alternate: {
          label: 'Ver outros cursos',
          type: 'navigate',
          url: modal.dataset.productsUrl || '/collections/all',
        },
        continue: moodleAction,
      };
    }

    return {
      checkoutRequested,
      alternate: {
        label: 'Revisar itens',
        type: 'navigate',
        url: modal.dataset.cartUrl || '/cart',
      },
      continue: {
        label: 'Continuar',
        ...continueAction,
      },
    };
  }

  async function runModalAction(modal, action) {
    if (!action || modalActionInProgress) return;

    if (action.type === 'continue') {
      closeModal(modal, 'continue');

      if (!modalActionState?.checkoutRequested) {
        navigateTo(action.url || modal.dataset.homeUrl);
      }
      return;
    }

    if (action.type === 'logout-and-navigate') {
      modalActionInProgress = true;
      const previousContext = captureRegistrationContext(modal);
      let cartContextUpdateAttempted = false;

      setModalProcessing(modal, true);

      try {
        const registrationType = String(action.registrationType || 'PF').toUpperCase() === 'PJ'
          ? 'PJ'
          : 'PF';
        cartContextUpdateAttempted = true;
        await prepareGuestContext(registrationType);

        const response = await fetchWithTimeout(action.logoutUrl, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error(`Não foi possível encerrar a sessão (${response.status})`);
        }

        completeAcknowledgement('navigate', true);
        window.location.replace(action.url);
      } catch (error) {
        console.error('[cart-validation-modal] Falha ao encerrar a sessão.', error);

        if (cartContextUpdateAttempted) {
          try {
            if (previousContext.cartContext) {
              await prepareGuestContext(previousContext.cartContext);
            }
          } catch (rollbackError) {
            console.error(
              '[cart-validation-modal] Falha ao restaurar o contexto anterior do carrinho.',
              rollbackError
            );
          }
        }

        setModalProcessing(modal, false);
        setModalError(
          modal,
          'Não foi possível preparar seu cadastro como Trabalhador. Verifique sua conexão e tente novamente.'
        );
        modal.querySelector('[data-cart-validation-error]')?.focus({ preventScroll: true });
        modalActionInProgress = false;
      }
      return;
    }

    closeModal(modal, 'navigate');
    navigateTo(action.url);
  }

  function getGroupedResults(contextResult, purchaseResult) {
    const contextRemovedItems = Array.isArray(purchaseResult.contextRemovedItems)
      ? purchaseResult.contextRemovedItems
      : Array.isArray(contextResult.removedItems)
        ? contextResult.removedItems
        : [];
    const purchaseRemovedItems = Array.isArray(purchaseResult.removedItems)
      ? purchaseResult.removedItems
      : [];
    const adjustedItems = Array.isArray(purchaseResult.adjustedItems)
      ? purchaseResult.adjustedItems
      : [];

    return {
      context: contextRemovedItems,
      purchased: purchaseRemovedItems.filter((item) => item.reason === 'already_purchased'),
      missingMoodle: purchaseRemovedItems.filter((item) => item.reason === 'missing_moodle'),
      duplicates: purchaseRemovedItems.filter((item) => item.reason === 'duplicate_in_cart'),
      adjusted: adjustedItems.filter((item) => item.reason === 'quantity_reduced'),
    };
  }

  function createProductItem(item) {
    const listItem = document.createElement('li');
    listItem.className = 'company-purchase-modal__item';

    if (item.imageUrl) {
      const image = document.createElement('img');
      image.className = 'company-purchase-modal__item-image';
      image.src = item.imageUrl;
      image.alt = '';
      image.width = 56;
      image.height = 56;
      listItem.appendChild(image);
    }

    const content = document.createElement('div');
    content.className = 'company-purchase-modal__item-content';

    const title = item.url ? document.createElement('a') : document.createElement('span');
    title.className = 'company-purchase-modal__item-title';
    title.textContent = item.title || 'Produto';
    if (item.url) title.href = item.url;
    content.appendChild(title);

    if (item.reason === 'quantity_reduced' && Number(item.previousQuantity) > 1) {
      const detail = document.createElement('span');
      detail.className = 'company-purchase-modal__item-detail';
      detail.textContent = `Quantidade alterada de ${item.previousQuantity} para 1`;
      content.appendChild(detail);
    }

    listItem.appendChild(content);
    return listItem;
  }

  function renderGroups(container, groupedResults) {
    container.replaceChildren();

    groupDefinitions.forEach((definition) => {
      const items = groupedResults[definition.key];
      if (!Array.isArray(items) || items.length === 0) return;

      const section = document.createElement('section');
      section.className = 'company-purchase-modal__group';

      const heading = document.createElement('h3');
      heading.className = 'company-purchase-modal__group-title';
      heading.textContent = definition.title;
      section.appendChild(heading);

      const list = document.createElement('ul');
      list.className = 'company-purchase-modal__items';
      if (items.length > 3) {
        list.classList.add('company-purchase-modal__items--scrollable');
        list.setAttribute('tabindex', '0');
        list.setAttribute(
          'aria-label',
          `${definition.title}: ${items.length} produtos. Use a rolagem para visualizar todos.`
        );
      }
      items.forEach((item) => list.appendChild(createProductItem(item)));
      section.appendChild(list);
      container.appendChild(section);
    });
  }

  function getModalCopy(groupedResults, customerType) {
    const hasContext = groupedResults.context.length > 0;
    const hasPurchased = groupedResults.purchased.length > 0;
    const hasOtherChanges =
      groupedResults.missingMoodle.length > 0 ||
      groupedResults.duplicates.length > 0 ||
      groupedResults.adjusted.length > 0;
    const remainingItemCount = Number(window.ToolboxCartPurchaseValidationPromise?.cart?.item_count || 0);

    if (hasContext && !hasPurchased && !hasOtherChanges) {
      const normalizedCustomerType = String(customerType || '').trim().toUpperCase();
      const contextItemCount = groupedResults.context.length;

      return {
        title: 'Atualizamos seus itens',
        description: normalizedCustomerType === 'PJ'
          ? `O conteúdo abaixo é para o perfil Trabalhador e foi retirado. Para acessar, entre ou crie um cadastro como Trabalhador e selecione novamente o que deseja acessar.`
          : 'O Acesso Corporativo à Eleva é voltado para empresas. Por isso, esse item foi retirado. Os demais itens continuam disponíveis.',
      };
    }

    if (hasPurchased && !hasContext && !hasOtherChanges ) {
      return {
        title: 'Esse conteúdo já está disponível para você',
        description: remainingItemCount > 0
          ? `O conteúdo abaixo já foi adquirido e pode ser acessado pela Escola Digital. Para evitar duplicidade, ele foi retirado. Os demais conteúdos continuam disponíveis.`
          : 'Você já adquiriu o conteúdo abaixo e pode acessá-lo pela Escola Digital. Para evitar duplicidade, ele foi retirado.',
      };
    }

    if (!hasContext && !hasPurchased && groupedResults.adjusted.length > 0 &&
        groupedResults.missingMoodle.length === 0 && groupedResults.duplicates.length === 0) {
      return {
        title: 'Ajustamos as quantidades do seu carrinho',
        description: 'Cada produto pode ser adquirido em apenas uma unidade.',
      };
    }

    return {
      title: 'Atualizamos seu carrinho',
      description: 'Alguns itens precisaram ser removidos ou ajustados antes de continuar.',
    };
  }

  function openModal(modal, groupedResults, purchaseResult) {
    const titleElement = modal.querySelector('[data-cart-validation-title]');
    const descriptionElement = modal.querySelector('[data-cart-validation-description]');
    const groupsElement = modal.querySelector('[data-cart-validation-groups]');
    const continueButton = modal.querySelector('[data-cart-validation-continue]');
    const alternateButton = modal.querySelector('[data-cart-validation-alternate]');
    const copy = getModalCopy(groupedResults, purchaseResult?.customerType);
    const actions = getModalActions(modal, groupedResults, purchaseResult);

    modalActionInProgress = false;
    setModalProcessing(modal, false);
    setModalError(modal);

    titleElement.textContent = copy.title;
    descriptionElement.textContent = copy.description;
    renderGroups(groupsElement, groupedResults);

    alternateButton.textContent = actions.alternate.label;
    continueButton.textContent = actions.continue.label;
    modalActionState = actions;

    lastFocusedElement = document.activeElement;
    modal.hidden = false;
    modal.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    continueButton.focus();
  }

  function bindModal(modal) {
    if (!modal || modal.dataset.purchaseModalBound === 'true') return;
    modal.dataset.purchaseModalBound = 'true';

    modal.addEventListener('click', (event) => {
      if (modalActionInProgress) return;

      if (event.target.closest('[data-cart-validation-continue]')) {
        runModalAction(modal, modalActionState?.continue).catch((error) => {
          console.error('[cart-validation-modal] Falha ao executar a ação principal.', error);
        });
        return;
      }

      if (event.target.closest('[data-cart-validation-alternate]')) {
        runModalAction(modal, modalActionState?.alternate).catch((error) => {
          console.error('[cart-validation-modal] Falha ao executar a ação alternativa.', error);
        });
        return;
      }

      if (event.target.closest('[data-company-purchase-modal-close]')) {
        closeModal(modal, 'stay');
      }
    });
  }

  async function initialize() {
    const modal = document.querySelector(modalSelector);
    bindModal(modal);

    if (!window.ToolboxCartContextValidationPromise || !window.ToolboxCartPurchaseValidationPromise) {
      completeAcknowledgement('continue', false);
      return;
    }

    const contextResult = await window.ToolboxCartContextValidationPromise;
    if (!contextResult || contextResult.status === 'error' || contextResult.reloadRequired === true) return;

    const purchaseResult = await window.ToolboxCartPurchaseValidationPromise;
    if (!purchaseResult || purchaseResult.status === 'error' || purchaseResult.reloadRequired === true) return;

    const groupedResults = getGroupedResults(contextResult, purchaseResult);
    const hasChanges = Object.values(groupedResults).some((items) => items.length > 0);

    if (!hasChanges || !modal) {
      completeAcknowledgement('continue', false);
      return;
    }

    openModal(modal, groupedResults, purchaseResult);
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const modal = document.querySelector(`${modalSelector}.is-open`);
    if (modal && !modalActionInProgress) closeModal(modal, 'stay');
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
